import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getSession } from '~/lib/auth';

/**
 * Business settings — the operational knobs behind the site + booking:
 *
 *   · Business info (name / phone / email / address / service area) —
 *     pushed to the public site + used in booking emails.
 *   · Booking (slot length, buffer, notice, horizon, weekly hours).
 *   · Google Calendar (connect status, which calendars block availability,
 *     which calendar receives bookings).
 *   · Spam protection (dev only) — the Cloudflare Turnstile keys.
 *
 * Autosaves like the Pages editor: edits debounce into a bulk PATCH.
 */

const API_BASE = import.meta.env.VITE_API_URL ?? 'https://api.valleyvitalitywellness.com';
const SAVE_DEBOUNCE_MS = 900;

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

interface DayWindow {
  start: string;
  end: string;
}

interface CalendarEntry {
  id: string;
  summary: string;
  primary: boolean;
  accessRole: string;
}

interface CalendarsState {
  connected: boolean;
  account_email: string | null;
  can_write: boolean;
  calendars: CalendarEntry[];
  error?: string;
}

const INPUT: React.CSSProperties = {
  width: '100%',
  fontFamily: 'inherit',
  fontSize: 14,
  padding: '10px 12px',
  border: '1px solid var(--line)',
  borderRadius: 8,
  background: 'var(--card)',
  color: 'var(--text)',
};

const LABEL: React.CSSProperties = {
  fontSize: 12,
  fontWeight: 600,
  letterSpacing: '0.06em',
  color: 'var(--primary)',
  textTransform: 'uppercase',
  display: 'block',
  marginBottom: 4,
};

function authHeaders(): Record<string, string> {
  const session = getSession();
  return session
    ? { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' }
    : { 'Content-Type': 'application/json' };
}

function Section({ title, sub, children }: { title: string; sub?: string; children: React.ReactNode }) {
  return (
    <section style={{
      background: 'var(--card)', border: '1px solid var(--line-soft)',
      borderRadius: 14, padding: '20px 22px', marginBottom: 16, maxWidth: 760,
    }}>
      <h2 style={{ fontSize: 19, color: 'var(--primary)', marginBottom: 4 }}>{title}</h2>
      {sub && <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 16, lineHeight: 1.5 }}>{sub}</p>}
      {children}
    </section>
  );
}

export function SettingsPage() {
  const isDev = getSession()?.user.role === 'dev';
  const [values, setValues] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [dirtyKeys, setDirtyKeys] = useState<Set<string>>(new Set());
  const [calendars, setCalendars] = useState<CalendarsState | null>(null);
  const dirtyRef = useRef<Record<string, string>>({});
  const timer = useRef<number | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const [sRes, cRes] = await Promise.all([
          fetch(`${API_BASE}/api/admin/settings`, { headers: authHeaders() }),
          fetch(`${API_BASE}/api/admin/settings/calendars`, { headers: authHeaders() }),
        ]);
        if (!sRes.ok) throw new Error(`Settings load failed (${sRes.status})`);
        const sData = (await sRes.json()) as { settings: Record<string, string> };
        setValues(sData.settings);
        if (cRes.ok) setCalendars((await cRes.json()) as CalendarsState);
      } catch (e) {
        setError((e as Error).message);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const flush = useCallback(async () => {
    const payload = { ...dirtyRef.current };
    if (Object.keys(payload).length === 0) return;
    dirtyRef.current = {};
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/settings`, {
        method: 'PATCH',
        headers: authHeaders(),
        body: JSON.stringify({ values: payload }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(body.error || `Save failed (${res.status})`);
      }
      setDirtyKeys((prev) => {
        const next = new Set(prev);
        for (const k of Object.keys(payload)) next.delete(k);
        return next;
      });
      setSavedAt(Date.now());
      setError(null);
    } catch (e) {
      // Put the failed payload back so retry picks it up.
      dirtyRef.current = { ...payload, ...dirtyRef.current };
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  }, []);

  const set = useCallback(
    (key: string, value: string) => {
      setValues((prev) => ({ ...prev, [key]: value }));
      dirtyRef.current[key] = value;
      setDirtyKeys((prev) => new Set(prev).add(key));
      if (timer.current) window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => void flush(), SAVE_DEBOUNCE_MS);
    },
    [flush],
  );

  const hours = useMemo<Record<number, DayWindow | null>>(() => {
    try {
      const raw = JSON.parse(values.booking_hours ?? '{}') as Record<string, DayWindow | null>;
      const out: Record<number, DayWindow | null> = {};
      for (let d = 0; d <= 6; d++) out[d] = raw[String(d)] ?? null;
      return out;
    } catch {
      return { 0: null, 1: null, 2: null, 3: null, 4: null, 5: null, 6: null };
    }
  }, [values.booking_hours]);

  function setHours(day: number, window: DayWindow | null) {
    const next: Record<string, DayWindow | null> = {};
    for (let d = 0; d <= 6; d++) next[String(d)] = d === day ? window : hours[d] ?? null;
    set('booking_hours', JSON.stringify(next));
  }

  const busyCalendars = useMemo<string[]>(() => {
    try {
      const raw = JSON.parse(values.gcal_busy_calendars ?? '[]');
      return Array.isArray(raw) ? raw.filter((x: unknown) => typeof x === 'string') : [];
    } catch {
      return [];
    }
  }, [values.gcal_busy_calendars]);

  function toggleBusyCalendar(id: string) {
    const next = busyCalendars.includes(id)
      ? busyCalendars.filter((c) => c !== id)
      : [...busyCalendars, id];
    set('gcal_busy_calendars', JSON.stringify(next));
  }

  const statusChip = error ? (
    <span style={{ fontSize: 12, color: '#c44545' }}>⚠ {error}</span>
  ) : saving || dirtyKeys.size > 0 ? (
    <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Saving…</span>
  ) : savedAt ? (
    <span style={{ fontSize: 12, color: 'var(--teal)' }}>✓ All changes saved</span>
  ) : null;

  if (loading) {
    return <p style={{ color: 'var(--text-muted)' }}>Loading settings…</p>;
  }

  return (
    <div>
      <header style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 16, marginBottom: 18, maxWidth: 760 }}>
        <div>
          <h1 style={{ fontSize: 28, color: 'var(--primary)', marginBottom: 4 }}>Business settings</h1>
          <p style={{ color: 'var(--text-muted)', fontSize: 13.5, margin: 0 }}>
            Identity, hours, and booking behavior. Changes to business info reach the
            public site on the next Publish; booking changes apply immediately.
          </p>
        </div>
        {statusChip}
      </header>

      <Section title="Business info" sub="Used in booking emails and available to the public site.">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          <div>
            <label style={LABEL}>Business name</label>
            <input type="text" value={values.business_name ?? ''} onChange={(e) => set('business_name', e.target.value)} style={INPUT} />
          </div>
          <div>
            <label style={LABEL}>Phone</label>
            <input type="text" value={values.business_phone ?? ''} onChange={(e) => set('business_phone', e.target.value)} style={INPUT} />
          </div>
          <div>
            <label style={LABEL}>Email</label>
            <input type="email" value={values.business_email ?? ''} onChange={(e) => set('business_email', e.target.value)} style={INPUT} />
          </div>
          <div>
            <label style={LABEL}>Location / address line</label>
            <input type="text" value={values.business_address ?? ''} onChange={(e) => set('business_address', e.target.value)} style={INPUT} />
          </div>
        </div>
        <div style={{ marginTop: 14 }}>
          <label style={LABEL}>Service area (comma-separated)</label>
          <textarea value={values.service_area ?? ''} onChange={(e) => set('service_area', e.target.value)} rows={2} style={{ ...INPUT, resize: 'vertical', lineHeight: 1.5 }} />
        </div>
      </Section>

      <Section
        title="Booking"
        sub="The Book page offers two ways in: the free phone consultation (scheduled from the weekly hours below), and — once you add a link — signing up directly through your booking site."
      >
        <div style={{ marginBottom: 18 }}>
          <label style={LABEL}>Direct-signup link (your booking site)</label>
          <input
            type="text"
            value={values.booking_url ?? ''}
            onChange={(e) => set('booking_url', e.target.value)}
            placeholder="https://…  (leave empty to hide the direct-signup option)"
            style={INPUT}
          />
          <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '6px 0 0', lineHeight: 1.5 }}>
            Shown on the Book page as the second option (“Ready to start? Sign up directly”).
            The free-consult scheduler stays available either way. Leave empty to hide the
            direct-signup option. Publish to put the change live.
          </p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 18 }}>
          <div>
            <label style={LABEL}>Slot length (min)</label>
            <input type="number" value={values.booking_slot_minutes ?? '15'} onChange={(e) => set('booking_slot_minutes', e.target.value)} style={INPUT} />
          </div>
          <div>
            <label style={LABEL}>Buffer between (min)</label>
            <input type="number" value={values.booking_buffer_minutes ?? '15'} onChange={(e) => set('booking_buffer_minutes', e.target.value)} style={INPUT} />
          </div>
          <div>
            <label style={LABEL}>Min notice (hours)</label>
            <input type="number" value={values.booking_notice_hours ?? '12'} onChange={(e) => set('booking_notice_hours', e.target.value)} style={INPUT} />
          </div>
          <div>
            <label style={LABEL}>Days bookable ahead</label>
            <input type="number" value={values.booking_horizon_days ?? '14'} onChange={(e) => set('booking_horizon_days', e.target.value)} style={INPUT} />
          </div>
        </div>

        <label style={LABEL}>Weekly hours ({values.timezone ?? 'America/Anchorage'})</label>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {DAY_NAMES.map((name, day) => {
            const w = hours[day];
            return (
              <div key={day} style={{
                display: 'grid', gridTemplateColumns: '120px auto 1fr', gap: 12,
                alignItems: 'center', padding: '7px 10px',
                background: w ? 'var(--surface)' : 'transparent',
                border: '1px solid var(--line-soft)', borderRadius: 8,
              }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13.5, cursor: 'pointer', color: w ? 'var(--primary)' : 'var(--text-muted)' }}>
                  <input
                    type="checkbox"
                    checked={!!w}
                    onChange={(e) => setHours(day, e.target.checked ? { start: '12:00', end: '16:00' } : null)}
                  />
                  {name}
                </label>
                {w ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input type="time" value={w.start} onChange={(e) => setHours(day, { ...w, start: e.target.value })} style={{ ...INPUT, width: 130, padding: '6px 8px' }} />
                    <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>to</span>
                    <input type="time" value={w.end} onChange={(e) => setHours(day, { ...w, end: e.target.value })} style={{ ...INPUT, width: 130, padding: '6px 8px' }} />
                  </div>
                ) : (
                  <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>Closed</span>
                )}
                <span />
              </div>
            );
          })}
        </div>
      </Section>

      <Section
        title="Google Calendar"
        sub="Bookings are checked against the calendars you select (so a time that's busy anywhere never gets offered) and new consults are added to the target calendar."
      >
        {!calendars || !calendars.connected ? (
          <div>
            <p style={{ fontSize: 14, color: 'var(--text)', marginBottom: 12, lineHeight: 1.6 }}>
              {calendars?.account_email
                ? <>Connection for <b>{calendars.account_email}</b> needs to be refreshed{calendars.error ? ' (token expired or permissions changed)' : ''}.</>
                : 'No Google account connected yet. Booking still works off your weekly hours — connecting prevents double-booking against everything else on the calendar.'}
            </p>
            <a
              href={`${API_BASE}/api/auth/google/start`}
              className="btn"
              style={{
                display: 'inline-block', background: 'var(--primary)', color: 'var(--card)',
                padding: '9px 16px', borderRadius: 8, fontSize: 13.5, fontWeight: 600,
                textDecoration: 'none',
              }}
            >
              {calendars?.account_email ? 'Reconnect Google Calendar' : 'Connect Google Calendar'}
            </a>
          </div>
        ) : (
          <div>
            <p style={{ fontSize: 13.5, color: 'var(--text-muted)', marginBottom: 14 }}>
              Connected as <b style={{ color: 'var(--primary)' }}>{calendars.account_email}</b>
              {!calendars.can_write && (
                <>
                  {' '}— <span style={{ color: '#c44545' }}>read-only connection.</span>{' '}
                  <a href={`${API_BASE}/api/auth/google/start`} style={{ color: 'var(--teal)', fontWeight: 600 }}>
                    Reconnect
                  </a>{' '}
                  to let bookings create calendar events.
                </>
              )}
            </p>

            <label style={LABEL}>Calendars that block availability</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 16 }}>
              {calendars.calendars.map((cal) => (
                <label key={cal.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13.5, cursor: 'pointer', padding: '5px 8px', borderRadius: 6, background: busyCalendars.includes(cal.id) ? 'var(--surface)' : 'transparent' }}>
                  <input
                    type="checkbox"
                    checked={busyCalendars.includes(cal.id)}
                    onChange={() => toggleBusyCalendar(cal.id)}
                  />
                  {cal.summary}
                  {cal.primary && <span style={{ fontSize: 10.5, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>primary</span>}
                </label>
              ))}
            </div>

            <label style={LABEL}>Calendar that receives bookings</label>
            <select
              value={values.gcal_target_calendar ?? 'primary'}
              onChange={(e) => set('gcal_target_calendar', e.target.value)}
              style={{ ...INPUT, maxWidth: 380 }}
            >
              <option value="primary">Primary calendar</option>
              {calendars.calendars
                .filter((cal) => cal.accessRole === 'owner' || cal.accessRole === 'writer')
                .map((cal) => (
                  <option key={cal.id} value={cal.id}>{cal.summary}</option>
                ))}
            </select>
          </div>
        )}
      </Section>

      {isDev && (
        <Section
          title="Spam protection"
          sub="Cloudflare Turnstile on the contact form and the consult booking — on top of the hidden honeypot, which is always on. Paste BOTH keys to switch it on (no Publish needed); clear the secret to switch it off. Only HDM (dev) sees this section."
        >
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <div>
              <label style={LABEL}>Turnstile site key</label>
              <input
                type="text"
                value={values.turnstile_site_key ?? ''}
                onChange={(e) => set('turnstile_site_key', e.target.value.trim())}
                placeholder="0x4AAAA…"
                style={INPUT}
              />
            </div>
            <div>
              <label style={LABEL}>Turnstile secret key</label>
              <input
                type="password"
                autoComplete="off"
                value={values.turnstile_secret_key ?? ''}
                onChange={(e) => set('turnstile_secret_key', e.target.value.trim())}
                placeholder="0x4AAAA…"
                style={INPUT}
              />
            </div>
          </div>
          <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '10px 0 0', lineHeight: 1.5 }}>
            Cloudflare dashboard → Turnstile → Add widget: hostname{' '}
            <code>valleyvitalitywellness.com</code>, widget mode Managed. The secret never leaves
            the API — admins and editors can't read it, and it's redacted in the activity log.
          </p>
        </Section>
      )}
    </div>
  );
}
